import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Skeleton,
  Snackbar,
  Switch,
  TextField,
  Typography,
  FormHelperText,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../../services/HttpService";
import { useFormik } from "formik";
import * as yup from "yup";
import UserService from "../../services/UserService";
import throttle from "lodash/throttle";
import debounce from "lodash/debounce";
import parse from "autosuggest-highlight/parse";
import Labels from "../../labels/Labels";
import CustomFieldRenderer from "../../components/CustomFieldRenderer";

const autocompleteService = { current: null };



export default function AddOrganizationalUnit(props) {
  const [addLocation, setAddLocation] = useState(false);

  // Location state variables
  const [options, setOptions] = useState([]);
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [address_line_1, setaddress_line_1] = useState("");
  const [address_line_2, setaddress_line_2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setzip] = useState("");
  const [first_name, setfirst_name] = useState("");
  const [last_name, setlast_name] = useState("");
  const [email, setemail] = useState("");
  const [phone, setphone] = useState("");
  const [external_id, setexternal_id] = useState("");
  const [username, setusername] = useState("");

  // Existing location search state
  const [locationMode, setLocationMode] = useState("create"); // "create" or "select"
  const [locationSearchQuery, setLocationSearchQuery] = useState("");
  const [debouncedLocationSearchQuery, setDebouncedLocationSearchQuery] = useState("");
  const [selectedExistingLocation, setSelectedExistingLocation] = useState(null);

  // Google Maps autocomplete service functions
  const fetch = useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 400),
    []
  );

  const fetchPlaceDetail = useMemo(
    () => (request, callback) => {
      if (request && request.place_id) {
        const getInfo = (address_components, key) => {
          for (let i = 0; i < address_components.length; i++) {
            if (address_components[i].types.includes(key))
              return address_components[i].short_name
                ? address_components[i].short_name
                : "";
          }
          return "";
        };

        const service = new window.google.maps.places.PlacesService(
          document.createElement("div")
        );
        service.getDetails({ placeId: request.place_id }, syncWithForm);

        function syncWithForm(place, status) {
          setFormikSynced(
            "location.address_line_1",
            getInfo(place.address_components, "street_number") +
              " " +
              getInfo(place.address_components, "route"),
            setaddress_line_1
          );
          setFormikSynced(
            "location.address_line_2",
            getInfo(place.address_components, "subpremise"),
            setaddress_line_2
          );
          setFormikSynced(
            "location.city",
            getInfo(place.address_components, "locality"),
            setCity
          );
          setFormikSynced(
            "location.state",
            getInfo(place.address_components, "administrative_area_level_1"),
            setState
          );
          setFormikSynced(
            "location.zip",
            getInfo(place.address_components, "postal_code"),
            setzip
          );
        }
      }
    },
    []
  );

  // Load Google Maps script
  const loaded = useRef(false);

  function loadScript(src, position, id) {
    if (!position) {
      return;
    }

    const script = document.createElement("script");
    script.setAttribute("async", "");
    script.setAttribute("id", id);
    script.src = src;
    position.appendChild(script);
  }

  if (typeof window !== "undefined" && !loaded.current) {
    if (!document.querySelector("#google-maps")) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${window.GOOGLE_MAPS_API_KEY}&libraries=places`,
        document.querySelector("head"),
        "google-maps"
      );
    }

    loaded.current = true;
  }

  // Helper function to update formik values and state
  const setFormikSynced = (key, value, callback) => {
    formik.setFieldValue(key, value, false);
    callback(value);
  };

  // Helper function to populate form with existing location data
  const populateFormWithExistingLocation = (location) => {
    if (!location) return;

    setFormikSynced("location.description", location.description || "", () => {});
    setFormikSynced("location.address_line_1", location.address_line_1 || "", setaddress_line_1);
    setFormikSynced("location.address_line_2", location.address_line_2 || "", setaddress_line_2);
    setFormikSynced("location.city", location.city || "", setCity);
    setFormikSynced("location.state", location.state || "", setState);
    setFormikSynced("location.zip", location.zip || "", setzip);
    setFormikSynced("location.first_name", location.first_name || "", setfirst_name);
    setFormikSynced("location.last_name", location.last_name || "", setlast_name);
    setFormikSynced("location.email", location.email || "", setemail);
    setFormikSynced("location.phone", location.phone || "", setphone);
    setFormikSynced("location.external_id", location.external_id || "", setexternal_id);
    setFormikSynced("location.username", location.username || "", setusername);

    // Clear touched state for location fields to remove error messages
    formik.setTouched({
      ...formik.touched,
      location: {
        description: false,
        address_line_1: false,
        address_line_2: false,
        city: false,
        state: false,
        zip: false,
        first_name: false,
        last_name: false,
        email: false,
        phone: false,
        external_id: false,
        username: false,
      }
    });
  };

  const wrapperFormikHandleChanged = (event) => {
    formik.handleChange(event);
    let callback = () => {};
    if (event.target.id === "location.address_line_1")
      callback = setaddress_line_1;
    if (event.target.id === "location.address_line_2")
      callback = setaddress_line_2;
    if (event.target.id === "location.city") callback = setCity;
    if (event.target.id === "location.state") callback = setState;
    if (event.target.id === "location.zip") callback = setzip;
    if (event.target.id === "location.first_name") callback = setfirst_name;
    if (event.target.id === "location.last_name") callback = setlast_name;
    if (event.target.id === "location.email") callback = setemail;
    if (event.target.id === "location.phone") callback = setphone;
    if (event.target.id === "location.external_id") callback = setexternal_id;
    if (event.target.id === "location.username") callback = setusername;

    setFormikSynced(event.target.id, event.target.value, callback);
  };

  // Effects for Google Maps autocomplete
  useEffect(() => {
    fetchPlaceDetail(value);
  }, [value, fetchPlaceDetail]);

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window.google) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch(
      {
        input: inputValue,
        componentRestrictions: {
          country: ["us"],
        },
        fields: ["description", "place_id"],
      },
      (results) => {
        if (active) {
          let newOptions = [];

          if (value) {
            newOptions = [value];
          }

          if (results) {
            newOptions = [...newOptions, ...results];
          }

          setOptions(newOptions);
        }
      }
    );

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  // Debounce location search query to prevent excessive API calls and race conditions
  useEffect(() => {
    const debouncedUpdate = debounce(() => {
      setDebouncedLocationSearchQuery(locationSearchQuery);
    }, 800);

    debouncedUpdate();

    return () => {
      debouncedUpdate.cancel();
    };
  }, [locationSearchQuery]);

  // Helper function to generate friendly error messages
  const createFriendlyErrorMessage = (errorObj, customField) => {
    const fieldLabel = Labels.getLabel(errorObj.field, errorObj.field);

    let message = Labels.getLabel(
      `${errorObj.field}.${errorObj.errorCode}`,
      `${fieldLabel}: ${errorObj.message}`
    );

    return message;
  };

  const createOrganizationalUnitMutation = useMutation(
    (values) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/subdepartment`,
        values
      );
    },
    {
      onSuccess: () => {
        formik.setSubmitting(false);
        queryClient.invalidateQueries("organizationalunit");

        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "addOrganizationalUnit.successMessage",
            "Organizational Unit created successfully."
          ),
          onClose: () => {
            setFeedback({ open: false });
            props.handleClose();
          },
        });
      },
      onError: (err) => {
        formik.setSubmitting(false);

        if (err?.response?.status === 500) {
          setFeedback({
            open: true,
            type: "error",
            message: Labels.getLabel(
              "addOrganizationalUnit.errorMessage",
              "There was an error creating the organizational unit."
            ),
          });
        } else {
          // Handle field-level validation errors
          const validationErrors = err?.response?.data?.validationErrors;

          if (validationErrors && Array.isArray(validationErrors)) {
            // Set field errors for custom fields with friendly messages
            validationErrors.forEach((errorObj) => {
              const customField = customFields?.data?.fieldList?.find(
                (field) => field.key === errorObj.field
              );
              const friendlyMessage = createFriendlyErrorMessage(
                errorObj,
                customField
              );
              formik.setFieldError(
                `customFields.${errorObj.field}`,
                friendlyMessage
              );
            });
          }

            const defaultMessage = err?.response?.data ? err.response.data : err.message;

            const msg = err?.response?.data?.validationMessage
            ? err?.response?.data?.validationMessage
            : defaultMessage;
          setFeedback({
            open: true,
            type: "error",
            message: msg,
          });
        }
      },
    }
  );

  async function fetchCompanyData() {
    let fetchURL;
    fetchURL =
      window.API_URL +
      `/employer/all?employerType=COMPANY&onlyParentEmployer=true`;

    const response = await HttpService.getAxiosClient().get(fetchURL);
    return response;
  }

  const { data: companyData, isLoading: isLoadingCompanies } = useQuery({
    queryKey: ["companies"],
    queryFn: fetchCompanyData,
  });

  // Fetch custom fields based on selected company
  const [selectedEmployerId, setSelectedEmployerId] = useState(null);
  const { data: customFields, isLoading: isLoadingCustomFields, error: customFieldsError } = useQuery({
    queryKey: ["customFields", selectedEmployerId],
    queryFn: () => fetchCustomFieldsData(selectedEmployerId),
    enabled: !!selectedEmployerId,
    retry: 2,
  });

  // Update addLocation based on showLocationForm from backend
  useEffect(() => {
    if (customFields?.data?.metadata?.showLocationForm !== undefined) {
      setAddLocation(customFields.data.metadata.showLocationForm);
    }
  }, [customFields]);


  // Fetch existing locations with debounced search query
  const { data: existingLocations, isLoading: isLoadingLocations } = useQuery({
    queryKey: ["locations-search", debouncedLocationSearchQuery, selectedEmployerId],
    queryFn: async () => {
      if (!debouncedLocationSearchQuery || debouncedLocationSearchQuery.length < 2) return [];
      const response = await HttpService.getAxiosClient().get(
        window.API_URL + `/locations/search?query=${encodeURIComponent(debouncedLocationSearchQuery)}`
      );
      return response.data;
    },
    enabled: !!selectedEmployerId && locationMode === "select" && debouncedLocationSearchQuery.length >= 2,
  });

  // Create dynamic validation schema that includes custom fields validation
  const validationSchema = useMemo(() => {
    const createCustomFieldsValidation = () => {
      const customFieldList = customFields?.data?.fieldList || [];
      const customFieldsValidationShape = {};

      customFieldList.forEach((customField) => {
        let customFieldValidation;
        let fieldLabel = Labels.getLabel(customField.key, customField.key);

        if (customField.type === "SELECT") {
          // For SELECT fields, we now store objects with value/description
          customFieldValidation = yup.object().shape({
            value: yup.string(),
            description: yup.string(),
          }).nullable();

          if (customField.required) {
            customFieldValidation = customFieldValidation.test(
              'required',
              Labels.getLabel(
                `${customField.key}.required`,
                `${fieldLabel} is required`
              ),
              (value) => {
                return value && value.value && value.value.trim() !== '';
              }
            );
          }
        } else if (customField.type === "NUMBER") {
          // NUMBER fields remain as numbers
          customFieldValidation = yup.number();
          if (customField.required) {
            customFieldValidation = customFieldValidation.required(
              Labels.getLabel(
                `${customField.key}.required`,
                `${fieldLabel} is required`
              )
            );
          }
        } else {
          // TEXT fields remain as strings
          customFieldValidation = yup.string();

          if (customField.required) {
            customFieldValidation = customFieldValidation.required(
              Labels.getLabel(
                `${customField.key}.required`,
                `${fieldLabel} is required`
              )
            );
          }

          if (customField.validation?.maxLength) {
            customFieldValidation = customFieldValidation.max(
              customField.validation.maxLength,
              Labels.getLabel(
                `${customField.key}.maxLength`,
                `${fieldLabel} must be at most ${customField.validation.maxLength} characters`
              )
            );
          }
        }

        customFieldsValidationShape[customField.key] = customFieldValidation;
      });

      return yup.object().shape(customFieldsValidationShape);
    };

    return yup.object({
      companiesList: yup
        .object()
        .nullable()
        .required(
          Labels.getLabel(
            "addOrganizationalUnit.validation.companyRequired",
            "Company is required"
          )
        ),
      name: yup
        .string(
          Labels.getLabel(
            "addOrganizationalUnit.validation.enterUnitName",
            "Enter the name of the Unit"
          )
        )
        .required(
          Labels.getLabel(
            "addOrganizationalUnit.validation.unitNameRequired",
            "Unit name is required"
          )
        ),
      description: yup
        .string(
          Labels.getLabel(
            "addOrganizationalUnit.validation.enterDescription",
            "Enter the description"
          )
        )
        .required(
          Labels.getLabel(
            "addOrganizationalUnit.validation.descriptionRequired",
            "Description is required"
          )
        ),
      customFields: createCustomFieldsValidation(),
      location: yup
        .object()
        .shape({
          description: yup
            .string(
              Labels.getLabel(
                "addOrganizationalUnit.validation.enterLocationDescription",
                "Enter the description for this location"
              )
            )
            .when([], {
              is: () => addLocation,
              then: yup
                .string()
                .required(
                  Labels.getLabel(
                    "addOrganizationalUnit.validation.descriptionRequired",
                    "A description is required"
                  )
                ),
            }),
          address_line_1: yup
            .string(
              Labels.getLabel(
                "addOrganizationalUnit.validation.enterAddressLine1",
                "Enter the Address Line 1"
              )
            )
            .when([], {
              is: () => addLocation,
              then: yup
                .string()
                .required(
                  Labels.getLabel(
                    "addOrganizationalUnit.validation.addressLine1Required",
                    "Address line 1 is required"
                  )
                ),
            }),
          address_line_2: yup.string(
            Labels.getLabel(
              "addOrganizationalUnit.validation.enterAddressLine2",
              "Enter the Address Line 2"
            )
          ),
          city: yup
            .string(
              Labels.getLabel(
                "addOrganizationalUnit.validation.enterCity",
                "Enter the city"
              )
            )
            .when([], {
              is: () => addLocation,
              then: yup
                .string()
                .required(
                  Labels.getLabel(
                    "addOrganizationalUnit.validation.cityRequired",
                    "City is required"
                  )
                ),
            }),
          state: yup
            .string(
              Labels.getLabel(
                "addOrganizationalUnit.validation.enterState",
                "Enter the state"
              )
            )
            .when([], {
              is: () => addLocation,
              then: yup
                .string()
                .required(
                  Labels.getLabel(
                    "addOrganizationalUnit.validation.stateRequired",
                    "State is required"
                  )
                ),
            }),
          zip: yup
            .string(
              Labels.getLabel(
                "addOrganizationalUnit.validation.enterZip",
                "Enter the zip code"
              )
            )
            .when([], {
              is: () => addLocation,
              then: yup
                .string()
                .required(
                  Labels.getLabel(
                    "addOrganizationalUnit.validation.zipRequired",
                    "Zip is required"
                  )
                ),
            }),
          first_name: yup
            .string(
              Labels.getLabel(
                "addOrganizationalUnit.validation.enterFirstName",
                "Enter the first name of the inventory manager"
              )
            )
            .when([], {
              is: () => addLocation,
              then: yup
                .string()
                .required(
                  Labels.getLabel(
                    "addOrganizationalUnit.validation.firstNameRequired",
                    "First name is required"
                  )
                ),
            }),
          last_name: yup
            .string(
              Labels.getLabel(
                "addOrganizationalUnit.validation.enterLastName",
                "Enter the last name of the inventory manager"
              )
            )
            .when([], {
              is: () => addLocation,
              then: yup
                .string()
                .required(
                  Labels.getLabel(
                    "addOrganizationalUnit.validation.lastNameRequired",
                    "Last name is required"
                  )
                ),
            }),
          email: yup
            .string(
              Labels.getLabel(
                "addOrganizationalUnit.validation.enterEmail",
                "Enter the email of the inventory manager"
              )
            )
            .when([], {
              is: () => addLocation,
              then: yup
                .string()
                .required(
                  Labels.getLabel(
                    "addOrganizationalUnit.validation.emailRequired",
                    "Email is required"
                  )
                ),
            }),
          phone: yup
            .string(
              Labels.getLabel(
                "addOrganizationalUnit.validation.enterPhone",
                "Enter the phone of the inventory manager"
              )
            )
            .when([], {
              is: () => addLocation,
              then: yup
                .string()
                .required(
                  Labels.getLabel(
                    "addOrganizationalUnit.validation.phoneRequired",
                    "Phone is required"
                  )
                ),
            }),
        })
        .nullable(),
    });
  }, [customFields?.data?.fieldList, addLocation]);

  const formik = useFormik({
    initialValues: {
      companiesList: null,
      id: "",
      name: "",
      description: "",
      location: {
        description: "",
        address_line_1: "",
        address_line_2: "",
        city: "",
        state: "",
        zip: "",
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        external_id: "",
        username: "",
      },
    },
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      // Prevent multiple submissions
      setSubmitting(true);

      const transformedValues = {
        ...values,
        idEmployer: values.companiesList?.idEmployer || null, // Extract just the idEmployer
      };

      // Handle location data
      if (!addLocation) {
        delete transformedValues.location;
      } else if (locationMode === "select" && selectedExistingLocation) {
        // If using existing location, send location ID instead of full location data
        transformedValues.idLocation = selectedExistingLocation.id || selectedExistingLocation.idLocation;
        delete transformedValues.location;
      }

      // Include custom fields if they exist and transform for backend
      if (values.customFields && Object.keys(values.customFields).length > 0) {
          transformedValues.customFields = values.customFields;
        // const transformedCustomFields = {};
        //
        // Object.keys(values.customFields).forEach(key => {
        //   const fieldData = values.customFields[key];
        //
        //   // Handle different field types
        //   if (fieldData && typeof fieldData === 'object' && fieldData.value !== undefined) {
        //     // SELECT fields - extract value for backend, but you also have description available
        //     transformedCustomFields[key] = fieldData.value;
        //
        //     // Optional: If backend needs both value and description, send object:
        //     // transformedCustomFields[key] = {
        //     //   value: fieldData.value,
        //     //   description: fieldData.description
        //     // };
        //   } else {
        //     // TEXT/NUMBER fields - send as is (string/number)
        //     transformedCustomFields[key] = fieldData;
        //   }
        // });
        
       // transformedValues.customFields = transformedCustomFields;
      }

      createOrganizationalUnitMutation.mutate({ ...transformedValues });
    },
  });

  // Initialize custom fields in formik values when they're loaded
  useEffect(() => {
    if (customFields?.data?.fieldList && customFields.data.fieldList.length > 0) {
      const initialCustomFields = {};
      
      customFields.data.fieldList.forEach(field => {
        // Initialize based on field type to match validation schema expectations
        const existingValue = formik.values.customFields[field.key];
        
        if (existingValue !== undefined) {
          // Keep existing value if it exists
          initialCustomFields[field.key] = existingValue;
        } else {
          // Initialize with appropriate empty value based on field type
          if (field.type === 'SELECT') {
            initialCustomFields[field.key] = null; // SELECT fields expect object or null
          } else if (field.type === 'NUMBER') {
            initialCustomFields[field.key] = ''; // NUMBER fields expect string initially
          } else {
            initialCustomFields[field.key] = ''; // TEXT fields expect string
          }
        }
      });
      
      // Only update if there are new fields or current fields are empty
      const currentKeys = Object.keys(formik.values.customFields);
      const newKeys = Object.keys(initialCustomFields);
      const hasNewFields = newKeys.some(key => !currentKeys.includes(key));
      
      if (currentKeys.length === 0 || hasNewFields) {
        formik.setValues(prev => ({
          ...prev,
          customFields: initialCustomFields
        }), false);
      }
    }
  }, [customFields?.data?.fieldList]);

  const queryClient = useQueryClient();

  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback({ ...feedback, open: false });
  };

  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  async function fetchCustomFieldsData(idEmployer) {
    if (!idEmployer) return { data: { fieldList: [] } };

    let fetchURL;
    fetchURL = window.API_URL + `/subdepartment/custom-fields/${idEmployer}`;

    const response = await HttpService.getAxiosClient().get(fetchURL);
    return response;
  }

  const employerID = UserService.getTokenClaim("employer_id");

  function getTransformedCompanyList(companyList) {
    //
    // if ("ANY" === sessionStorage.getItem('chosenEmployerId')) {
    //     return companyList;
    // }
    //
    // let transformedCompanyList = [];
    // let employersBeingOverseen = [];
    // for (const company of companyList) {
    //     if (Array.isArray(company.employersBeingOverseen) && company.employersBeingOverseen.length > 0) {
    //         employersBeingOverseen.push(...company.employersBeingOverseen);
    //     }
    // }
    //
    // transformedCompanyList = companyList.concat(employersBeingOverseen);
    // return transformedCompanyList;

    if ("ANY" === sessionStorage.getItem("chosenEmployerId")) {
      return companyList;
    }

    const employersBeingOverseen = companyList.flatMap(
      (company) => company.employersBeingOverseen || []
    );

    return [...companyList, ...employersBeingOverseen];
  }

  // Determine loading states for form readiness
  const isLoadingAnyData =
    isLoadingCompanies || (selectedEmployerId && isLoadingCustomFields);

  const isFormDisabled =
    isLoadingAnyData ||
    createOrganizationalUnitMutation.isLoading ||
    formik.isSubmitting;

  // Smart button state helper
  const getButtonState = () => {
    if (isLoadingCompanies) {
      return {
        disabled: true,
        text: Labels.getLabel(
          "addOrganizationalUnit.form.loadingCompanies",
          "Loading companies..."
        ),
      };
    }
    if (selectedEmployerId && isLoadingCustomFields) {
      return {
        disabled: true,
        text: Labels.getLabel(
          "addOrganizationalUnit.form.loadingFields",
          "Loading fields..."
        ),
      };
    }
    if (customFieldsError && selectedEmployerId) {
      return {
        disabled: true,
        text: Labels.getLabel(
          "addOrganizationalUnit.form.fieldsLoadError",
          "Unable to load required fields"
        ),
      };
    }
    if (createOrganizationalUnitMutation.isLoading) {
      return {
        disabled: true,
        text: Labels.getLabel("addOrganizationalUnit.form.saving", "Saving..."),
      };
    }
    if (formik.isSubmitting) {
      return {
        disabled: true,
        text: Labels.getLabel(
          "addOrganizationalUnit.form.processing",
          "Processing..."
        ),
      };
    }
    return {
      disabled: false,
      text: Labels.getLabel("addOrganizationalUnit.form.save", "SAVE"),
    };
  };

  return (
    <div>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={formik.handleSubmit}
        sx={{
          opacity: isLoadingAnyData ? 0.7 : 1,
          transition: "opacity 0.3s ease",
          pointerEvents: isLoadingAnyData ? "none" : "auto",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {/*Then modify the Autocomplete component*/}

            <Autocomplete
              fullWidth
              id="companiesList"
              name={"companiesList"}
              loading={isLoadingCompanies}
              disabled={isLoadingCompanies}
              options={
                !isLoadingCompanies
                  ? Array.isArray(companyData?.data)
                    ? getTransformedCompanyList(companyData?.data)
                    : []
                  : []
              }
              getOptionLabel={(option) =>
                `${option.description}-[${option.idEmployer}]`
              }
              filterSelectedOptions
              value={formik.values.companiesList}
              onChange={(event, selectedOption) => {
                formik.setFieldValue("companiesList", selectedOption);

                // Complete reset of custom fields when company changes
                formik.setFieldValue("customFields", {});

                // // Clear all custom field errors properly
                // const currentErrors = { ...formik.errors };
                // delete currentErrors.customFields;
                // formik.setErrors(currentErrors);
                //
                // // Clear all custom field touched states
                // const currentTouched = { ...formik.touched };
                // delete currentTouched.customFields;
                // formik.setTouched(currentTouched);


                  formik.setErrors(prev => {
                      const next = { ...prev };
                      delete next.customFields;         // removes all nested errors under customFields
                      return next;
                  });

                  formik.setTouched(prev => {
                      const next = { ...prev };
                      delete next.customFields;         // also clear touched to avoid lingering UI states
                      return next;
                  });

                // Reset location search state when company changes
                setLocationMode("create");
                setLocationSearchQuery("");
                setDebouncedLocationSearchQuery("");
                setSelectedExistingLocation(null);

                // Set selected employer ID for custom fields query
                setSelectedEmployerId(selectedOption?.idEmployer || null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={
                    formik.touched.companiesList &&
                    Boolean(formik.errors.companiesList)
                  }
                  helperText={
                    formik.touched.companiesList && formik.errors.companiesList
                  }
                  label={Labels.getLabel(
                    "addOrganizationalUnit.form.company",
                    "Company"
                  )}
                  placeholder={
                    isLoadingCompanies
                      ? Labels.getLabel(
                          "addOrganizationalUnit.form.loadingCompanies",
                          "Loading companies..."
                        )
                      : Labels.getLabel(
                          "addOrganizationalUnit.form.selectCompany",
                          "Select a company"
                        )
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingCompanies ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              id="name"
              name={"name"}
              label={Labels.getLabel(
                "addOrganizationalUnit.form.unitName",
                "Unit Name"
              )}
              value={formik.values.name}
              onChange={formik.handleChange}
              disabled={isLoadingCompanies}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="description"
              label={Labels.getLabel(
                "addOrganizationalUnit.form.description",
                "Description"
              )}
              value={formik.values.description}
              onChange={formik.handleChange}
              disabled={isLoadingCompanies}
              error={
                formik.touched.description && Boolean(formik.errors.description)
              }
              helperText={
                formik.touched.description && formik.errors.description
              }
            />
          </Grid>

          {/* Dynamic Custom Fields */}
          {selectedEmployerId && isLoadingCustomFields && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                  Additional Information
                </Typography>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={2}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    {Labels.getLabel(
                      "addOrganizationalUnit.form.loadingCustomFields",
                      "Loading additional fields..."
                    )}
                  </Typography>
                </Box>
              </Grid>
            </>
          )}
          {customFieldsError && selectedEmployerId && (
            <Grid item xs={12}>
              <Alert severity="error">
                {Labels.getLabel(
                  "addOrganizationalUnit.customFieldsError",
                  "Failed to load required fields. Please try selecting the company again or contact support if the problem persists."
                )}
              </Alert>
            </Grid>
          )}
          {customFields?.data?.fieldList &&
            customFields.data.fieldList.length > 0 &&
            !isLoadingCustomFields && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                    Additional Information
                  </Typography>
                  <Divider />
                </Grid>
                {customFields.data.fieldList
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((customField) => (
                    <CustomFieldRenderer
                      key={customField.key}
                      customField={customField}
                      formik={formik}
                    />
                  ))}
              </>
            )}

          <Grid item xs={12} sx={{ display: "none" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={addLocation}
                  disabled={isLoadingCompanies}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setAddLocation(checked);
                    if (!checked) {
                      // Reset location data when toggle is turned off
                      formik.setFieldValue("location", {
                        description: "",
                        address_line_1: "",
                        address_line_2: "",
                        city: "",
                        state: "",
                        zip: "",
                        first_name: "",
                        last_name: "",
                        email: "",
                        phone: "",
                        external_id: "",
                        username: "",
                      });
                      setValue(null);
                      setInputValue("");
                      setaddress_line_1("");
                      setaddress_line_2("");
                      setCity("");
                      setState("");
                      setzip("");
                      setfirst_name("");
                      setlast_name("");
                      setemail("");
                      setphone("");
                      setexternal_id("");
                      setusername("");
                    }
                  }}
                  name="addLocation"
                  color="primary"
                />
              }
              label={Labels.getLabel(
                "addOrganizationalUnit.form.addAddress",
                "Add Address"
              )}
            />
            <FormHelperText>
              {Labels.getLabel(
                "addOrganizationalUnit.form.addAddressHelp",
                "Only enable this option if you need to order cards."
              )}
            </FormHelperText>
          </Grid>
          {addLocation && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                  {Labels.getLabel(
                    "addOrganizationalUnit.form.deliveryDestination",
                    "Address to be used as the delivery destination for your card orders."
                  )}
                </Typography>
                <Divider />
              </Grid>
              {UserService.hasRole(["ou-manager"]) && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={locationMode === "select"}
                        onChange={(e) => {
                          const newMode = e.target.checked ? "select" : "create";
                          setLocationMode(newMode);
                          // Reset location data when switching modes
                          setLocationSearchQuery("");
                          setDebouncedLocationSearchQuery("");
                          setSelectedExistingLocation(null);
                          setValue(null);
                          setInputValue("");
                        }}
                        color="primary"
                      />
                    }
                    label={Labels.getLabel(
                      "addOrganizationalUnit.form.useExistingLocation",
                      "Select from existing locations"
                    )}
                  />
                </Grid>
              )}
              {locationMode === "select" ? (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {Labels.getLabel(
                      "addOrganizationalUnit.form.searchExistingLocations",
                      "Type to search existing locations (minimum 2 characters)"
                    )}
                  </Typography>
                  <Autocomplete
                    fullWidth
                    options={existingLocations || []}
                    loading={(isLoadingLocations && debouncedLocationSearchQuery.length >= 2) || (locationSearchQuery !== debouncedLocationSearchQuery && locationSearchQuery.length >= 2)}
                    filterOptions={(x) => x}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option.id === value.id;
                  }}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    return option.description || "";
                  }}
                  onInputChange={(event, value) => {
                    setLocationSearchQuery(value);
                    if (!value) {
                      setSelectedExistingLocation(null);
                    }
                  }}
                  onChange={(event, value) => {
                    if (value && typeof value === "object") {
                      // User selected an existing location
                      setSelectedExistingLocation(value);
                      populateFormWithExistingLocation(value);
                      // Clear Google Maps autocomplete
                      setValue(null);
                      setInputValue("");
                    } else {
                      // User is typing or cleared selection
                      setSelectedExistingLocation(null);
                      formik.setFieldValue("location.description", value || "");
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={Labels.getLabel(
                        "addOrganizationalUnit.form.locationDescription",
                        "Location Description"
                      )}
                      error={
                        formik.touched.location?.description &&
                        Boolean(formik.errors.location?.description)
                      }
                      helperText={
                        selectedExistingLocation
                          ? Labels.getLabel(
                              "addOrganizationalUnit.form.usingExistingLocation",
                              "Using existing location"
                            )
                          : formik.touched.location?.description &&
                            formik.errors.location?.description
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {((isLoadingLocations && debouncedLocationSearchQuery.length >= 2) ||
                              (locationSearchQuery !== debouncedLocationSearchQuery && locationSearchQuery.length >= 2)) && (
                              <CircularProgress color="inherit" size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ width: "100%" }}>
                        <Typography variant="body1" fontWeight="medium">
                          {option.description}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.address_line_1}
                          {option.address_line_2 && `, ${option.address_line_2}`}
                          {", "}
                          {option.city}, {option.state} {option.zip}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
                </Grid>
              ) : (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="location.description"
                      name="location.description"
                      label={Labels.getLabel(
                        "addOrganizationalUnit.form.locationDescription",
                        "Location Description"
                      )}
                      value={formik.values.location.description}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.location?.description &&
                        Boolean(formik.errors.location?.description)
                      }
                      helperText={
                        formik.touched.location?.description &&
                        formik.errors.location?.description
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                  <Autocomplete
                    id="google-control"
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.description
                    }
                    filterOptions={(x) => x}
                    options={options}
                    autoComplete
                    includeInputInList
                    filterSelectedOptions
                    value={value}
                    onChange={(event, newValue) => {
                      setOptions(newValue ? [newValue, ...options] : options);
                      setValue(newValue);
                    }}
                    onInputChange={(event, newInputValue) => {
                      setInputValue(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={Labels.getLabel(
                          "addOrganizationalUnit.form.enterLocation",
                          "Enter a location"
                        )}
                        helperText={Labels.getLabel(
                          "addOrganizationalUnit.form.googleMapsHelp",
                          "Search for address using Google Maps"
                        )}
                        fullWidth
                      />
                    )}
                    renderOption={(props, option) => {
                      const matches =
                        option.structured_formatting.main_text_matched_substrings;
                      const parts = parse(
                        option.structured_formatting.main_text,
                        matches.map((match) => [
                          match.offset,
                          match.offset + match.length,
                        ])
                      );

                      return (
                        <li {...props}>
                          <Grid container alignItems="center">
                            <Grid item>
                              <Box
                                component={LocationOnIcon}
                                sx={{ color: "text.secondary", mr: 2 }}
                              />
                            </Grid>
                            <Grid item xs>
                              {parts.map((part, index) => (
                                <span
                                  key={index}
                                  style={{
                                    fontWeight: part.highlight ? 700 : 400,
                                  }}
                                >
                                  {part.text}
                                </span>
                              ))}

                              <Typography variant="body2" color="text.secondary">
                                {option.structured_formatting.secondary_text}
                              </Typography>
                            </Grid>
                          </Grid>
                        </li>
                      );
                    }}
                  />
                  </Grid>
                </>
              )}
              {locationMode === "select" && selectedExistingLocation && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {Labels.getLabel(
                      "addOrganizationalUnit.form.existingLocationSelected",
                      "Using existing location:"
                    )}{" "}
                    <strong>{selectedExistingLocation.description}</strong>
                  </Alert>
                </Grid>
              )}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="location.address_line_1"
                  name="location.address_line_1"
                  label={Labels.getLabel(
                    "addOrganizationalUnit.form.addressLine1",
                    "Address line 1"
                  )}
                  value={address_line_1}
                  onChange={wrapperFormikHandleChanged}
                  disabled={locationMode === "select"}
                  error={
                    formik.touched.location?.address_line_1 &&
                    Boolean(formik.errors.location?.address_line_1)
                  }
                  helperText={
                    formik.touched.location?.address_line_1 &&
                    formik.errors.location?.address_line_1
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="location.address_line_2"
                  name="location.address_line_2"
                  label={Labels.getLabel(
                    "addOrganizationalUnit.form.addressLine2",
                    "Address line 2"
                  )}
                  value={address_line_2}
                  onChange={wrapperFormikHandleChanged}
                  disabled={locationMode === "select"}
                  error={
                    formik.touched.location?.address_line_2 &&
                    Boolean(formik.errors.location?.address_line_2)
                  }
                  helperText={
                    formik.touched.location?.address_line_2 &&
                    formik.errors.location?.address_line_2
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  id="location.city"
                  name="location.city"
                  label={Labels.getLabel(
                    "addOrganizationalUnit.form.city",
                    "City"
                  )}
                  value={city}
                  onChange={wrapperFormikHandleChanged}
                  disabled={locationMode === "select"}
                  error={
                    formik.touched.location?.city &&
                    Boolean(formik.errors.location?.city)
                  }
                  helperText={
                    formik.touched.location?.city &&
                    formik.errors.location?.city
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  id="location.state"
                  name="location.state"
                  label={Labels.getLabel(
                    "addOrganizationalUnit.form.state",
                    "State"
                  )}
                  value={state}
                  onChange={wrapperFormikHandleChanged}
                  disabled={locationMode === "select"}
                  error={
                    formik.touched.location?.state &&
                    Boolean(formik.errors.location?.state)
                  }
                  helperText={
                    formik.touched.location?.state &&
                    formik.errors.location?.state
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  id="location.zip"
                  name="location.zip"
                  label={Labels.getLabel(
                    "addOrganizationalUnit.form.zipCode",
                    "Zip Code"
                  )}
                  value={zip}
                  onChange={wrapperFormikHandleChanged}
                  disabled={locationMode === "select"}
                  error={
                    formik.touched.location?.zip &&
                    Boolean(formik.errors.location?.zip)
                  }
                  helperText={
                    formik.touched.location?.zip && formik.errors.location?.zip
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                  {Labels.getLabel(
                    "addOrganizationalUnit.form.inventoryManagerInfo",
                    "Inventory Manager Information"
                  )}
                </Typography>
                <Divider />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="location.first_name"
                  name="location.first_name"
                  label={Labels.getLabel(
                    "addOrganizationalUnit.form.firstName",
                    "First Name"
                  )}
                  value={first_name}
                  onChange={wrapperFormikHandleChanged}
                  disabled={locationMode === "select"}
                  error={
                    formik.touched.location?.first_name &&
                    Boolean(formik.errors.location?.first_name)
                  }
                  helperText={
                    formik.touched.location?.first_name &&
                    formik.errors.location?.first_name
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="location.last_name"
                  name="location.last_name"
                  label={Labels.getLabel(
                    "addOrganizationalUnit.form.lastName",
                    "Last Name"
                  )}
                  value={last_name}
                  onChange={wrapperFormikHandleChanged}
                  disabled={locationMode === "select"}
                  error={
                    formik.touched.location?.last_name &&
                    Boolean(formik.errors.location?.last_name)
                  }
                  helperText={
                    formik.touched.location?.last_name &&
                    formik.errors.location?.last_name
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="location.email"
                  name="location.email"
                  label={Labels.getLabel(
                    "addOrganizationalUnit.form.email",
                    "Email"
                  )}
                  value={email}
                  onChange={wrapperFormikHandleChanged}
                  disabled={locationMode === "select"}
                  error={
                    formik.touched.location?.email &&
                    Boolean(formik.errors.location?.email)
                  }
                  helperText={
                    formik.touched.location?.email &&
                    formik.errors.location?.email
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="location.phone"
                  name="location.phone"
                  label={Labels.getLabel(
                    "addOrganizationalUnit.form.phone",
                    "Phone"
                  )}
                  value={phone}
                  onChange={wrapperFormikHandleChanged}
                  disabled={locationMode === "select"}
                  error={
                    formik.touched.location?.phone &&
                    Boolean(formik.errors.location?.phone)
                  }
                  helperText={
                    formik.touched.location?.phone &&
                    formik.errors.location?.phone
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2">
                  {Labels.getLabel(
                    "addOrganizationalUnit.form.externalIdentifier",
                    "External Identifier (Optional)"
                  )}
                </Typography>
                <TextField
                  fullWidth
                  id="location.external_id"
                  name="location.external_id"
                  label={Labels.getLabel(
                    "addOrganizationalUnit.form.externalId",
                    "External ID"
                  )}
                  value={external_id}
                  onChange={wrapperFormikHandleChanged}
                  disabled={locationMode === "select"}
                  error={
                    formik.touched.location?.external_id &&
                    Boolean(formik.errors.location?.external_id)
                  }
                  helperText={
                    formik.touched.location?.external_id &&
                    formik.errors.location?.external_id
                  }
                />
              </Grid>
              <Grid item xs={6}>
                {UserService.hasRole(["pc-create-locations-user"]) && (
                  <>
                    <Typography variant="subtitle2">
                      {Labels.getLabel(
                        "addOrganizationalUnit.form.adminUsername",
                        "Admin username (Optional)"
                      )}
                    </Typography>
                    <TextField
                      fullWidth
                      id="location.username"
                      name="location.username"
                      label={Labels.getLabel(
                        "addOrganizationalUnit.form.username",
                        "Username"
                      )}
                      value={username}
                      onChange={wrapperFormikHandleChanged}
                      disabled={locationMode === "select"}
                      error={
                        formik.touched.location?.username &&
                        Boolean(formik.errors.location?.username)
                      }
                      helperText={
                        formik.touched.location?.username &&
                        formik.errors.location?.username
                      }
                    />
                  </>
                )}
              </Grid>
            </>
          )}
          <Grid item xs={12} pt={3}>
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={getButtonState().disabled}
              startIcon={
                getButtonState().disabled && (
                  <CircularProgress size={16} color="inherit" />
                )
              }
            >
              {getButtonState().text}
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
